'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { Button, cn, Input, toast } from '@crabs-ai/ds-react';
import { Close } from '@crabs-ai/web-icons';
import { Z_INDEX_EDITOR_DIALOG_OVERLAY } from '../z-indices';

const MIN_DURATION_MS = 50;
const MAX_DURATION_MS = 60_000;
const MIN_THRESHOLD_DB = -90;
const MAX_THRESHOLD_DB = 0;

const DEFAULT_THRESHOLD_DB = -40;

export interface RemoveSilenceDialogConfirmParams {
  minSilenceDurationMs: number;
  silenceThresholdDb: number;
}

interface RemoveSilenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (params: RemoveSilenceDialogConfirmParams) => void | Promise<void>;
}

export function RemoveSilenceDialog({
  open,
  onOpenChange,
  onConfirm,
}: RemoveSilenceDialogProps) {
  const t = useTranslations('Editor.timeline.remove-silence-dialog');
  const [minDurationInput, setMinDurationInput] = useState(
    String(MIN_DURATION_MS),
  );
  const [thresholdInput, setThresholdInput] = useState(
    String(DEFAULT_THRESHOLD_DB),
  );
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setMinDurationInput(String(MIN_DURATION_MS));
      setThresholdInput(String(DEFAULT_THRESHOLD_DB));
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  const handleConfirm = useCallback(() => {
    const minSilenceDurationMs = Number.parseInt(minDurationInput, 10);
    const silenceThresholdDb = Number.parseFloat(thresholdInput);

    if (
      Number.isNaN(minSilenceDurationMs) ||
      minSilenceDurationMs < MIN_DURATION_MS ||
      minSilenceDurationMs > MAX_DURATION_MS
    ) {
      toast({
        title: t('title'),
        description: t('invalid-duration'),
        type: 'error',
        toastOptions: { position: 'top-center' },
      });
      return;
    }

    if (
      Number.isNaN(silenceThresholdDb) ||
      silenceThresholdDb < MIN_THRESHOLD_DB ||
      silenceThresholdDb > MAX_THRESHOLD_DB
    ) {
      toast({
        title: t('title'),
        description: t('invalid-threshold'),
        type: 'error',
        toastOptions: { position: 'top-center' },
      });
      return;
    }

    onConfirm({ minSilenceDurationMs, silenceThresholdDb });
    onOpenChange(false);
  }, [minDurationInput, thresholdInput, onConfirm, onOpenChange, t]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onOpenChange(false);
      }
    },
    [onOpenChange],
  );

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-silence-dialog-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDEX_EDITOR_DIALOG_OVERLAY,
        pointerEvents: 'auto',
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-sys-overlay-dim animate-in fade-in-0"
        onClick={handleOverlayClick}
      />
      {/* Content */}
      <div
        ref={contentRef}
        className={cn(
          'absolute top-[50%] left-[50%] grid w-full translate-x-[-50%] translate-y-[-50%] gap-2xs rounded-md bg-background p-6 inset-shadow-thick-lg',
          'animate-in fade-in-0 zoom-in-95',
          'w-[400px] max-w-[90vw]',
        )}
        style={{ zIndex: 1 }}
      >
        <div className="flex flex-col gap-2 text-center sm:text-left">
          <h2
            id="remove-silence-dialog-title"
            className="text-lg leading-none font-semibold"
          >
            {t('title')}
          </h2>
        </div>
        <div className="flex flex-col gap-md">
          <p className="text-sys-cont-neutral-secondary text-body-small">
            {t('description')}
          </p>
          <div className="flex flex-col gap-2xs">
            <label
              className="text-label-medium text-sys-cont-neutral-secondary"
              htmlFor="remove-silence-min-ms"
            >
              {t('min-duration-label')}
            </label>
            <Input
              id="remove-silence-min-ms"
              type="number"
              inputMode="numeric"
              value={minDurationInput}
              onChange={(e) => setMinDurationInput(e.target.value)}
              min={MIN_DURATION_MS}
              max={MAX_DURATION_MS}
              step={10}
            />
            <span className="text-caption text-sys-cont-neutral-assistive">
              {t('min-duration-hint')}
            </span>
          </div>
          <div className="flex flex-col gap-2xs">
            <label
              className="text-label-medium text-sys-cont-neutral-secondary"
              htmlFor="remove-silence-threshold-db"
            >
              {t('threshold-label')}
            </label>
            <Input
              id="remove-silence-threshold-db"
              type="number"
              inputMode="decimal"
              value={thresholdInput}
              onChange={(e) => setThresholdInput(e.target.value)}
              min={MIN_THRESHOLD_DB}
              max={MAX_THRESHOLD_DB}
              step={1}
            />
            <span className="text-caption text-sys-cont-neutral-assistive">
              {t('threshold-hint')}
            </span>
          </div>
        </div>
        <div className="mt-md flex justify-end gap-2xs">
          <Button
            hierarchy="tertiary"
            size="medium"
            onClick={() => onOpenChange(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            hierarchy="primary"
            size="medium"
            onClick={handleConfirm}
          >
            {t('confirm')}
          </Button>
        </div>
        <button
          type="button"
          className="absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100"
          onClick={() => onOpenChange(false)}
        >
          <Close className="h-[24px]! w-[24px]! [&_path]:fill-sys-cont-neutral-default" />
          <span className="sr-only">Close</span>
        </button>
      </div>
    </div>,
    document.body,
  );
}
